import { useState } from 'react';
import { ExternalLink } from 'lucide-react';

// Portfolio data organized by categories
const portfolioData = {
  'Website Development': [
    {
      id: 2,
      image: '/portfolio/website/al-mahfil.png',
      title: 'Al Mahfil Restaurant',
      subtitle: 'Authentic Hyderabadi Cuisine in UAE',
      description: 'Crafted a modern, animated website with seamless Arabic/English translation. Developed a complete menu management system for easy admin updates, ensuring real-time content control. Delivered a responsive UI, optimized performance, and handled full deployment to live production.',
      color: 'bg-orange-600',
      link: 'https://www.almahfil.com/en'
    },
    {
      id: 3,
      image: '/portfolio/website/atlin-holding.png',
      title: 'Landing Page Design & Development',
      subtitle: 'Full-featured online store',
      description: 'Built a clean, modern landing page with responsive UI/UX, focusing on brand identity and seamless user experience.',
      color: 'bg-blue-600',
      link: 'http://www.atlinholding.com/'

    },
    {
      id: 4,
      image: '/portfolio/website/vinder.png',
      title: 'Vinder',
      subtitle: 'Single-Page Landing Page',
      description: 'Designed and coded a minimalist, high-conversion landing page with smooth interactions and mobile optimization.',
      color: 'bg-green-700',
      link: 'https://vinder-12239.web.app/'

    }
  ],
  'Brand Design': [
    {
      id: 5,
      image: '/api/placeholder/400/300',
      title: 'Brand Identity System',
      subtitle: 'Complete visual identity',
      description: 'Comprehensive brand package including logo, colors, and guidelines.',
      color: 'bg-purple-600'
    },
    {
      id: 6,
      image: '/api/placeholder/400/300',
      title: 'Logo Collection',
      subtitle: 'Various brand logos',
      description: 'Creative logo designs for different industries and businesses.',
      color: 'bg-pink-500'
    },
    {
      id: 7,
      image: '/api/placeholder/400/300',
      title: 'Packaging Design',
      subtitle: 'Product packaging',
      description: 'Eye-catching packaging design that stands out on shelves.',
      color: 'bg-orange-500'
    },
    {
      id: 8,
      image: '/api/placeholder/400/300',
      title: 'Corporate Branding',
      subtitle: 'Business identity',
      description: 'Professional branding for corporate clients and enterprises.',
      color: 'bg-teal-600'
    }
  ],
  'Social Media Posts': [
    {
      id: 9,
      image: '/api/placeholder/400/300',
      title: 'Instagram Campaign',
      subtitle: 'Social media graphics',
      description: 'Engaging Instagram posts and stories for brand awareness.',
      color: 'bg-gradient-to-br from-purple-600 to-pink-500'
    },
    {
      id: 10,
      image: '/api/placeholder/400/300',
      title: 'LinkedIn Content',
      subtitle: 'Professional posts',
      description: 'Professional LinkedIn graphics for B2B marketing campaigns.',
      color: 'bg-blue-700'
    },
    {
      id: 11,
      image: '/api/placeholder/400/300',
      title: 'Facebook Ads',
      subtitle: 'Advertisement graphics',
      description: 'High-converting Facebook ad creatives for various industries.',
      color: 'bg-blue-600'
    },
    {
      id: 12,
      image: '/api/placeholder/400/300',
      title: 'Twitter Graphics',
      subtitle: 'Tweet visuals',
      description: 'Eye-catching Twitter graphics for increased engagement.',
      color: 'bg-sky-500'
    }
  ],
  'Motion Graphics': [
    {
      id: 13,
      image: '/api/placeholder/400/300',
      title: 'Aerated Concrete',
      subtitle: 'Sale of aerated concrete',
      description: 'Dynamic motion graphics for construction industry marketing.',
      color: 'bg-yellow-400'
    },
    {
      id: 14,
      image: '/api/placeholder/400/300',
      title: 'III Assault Brigade',
      subtitle: 'Site design',
      description: 'Military-themed motion graphics with dramatic visual effects.',
      color: 'bg-gray-700'
    },
    {
      id: 15,
      image: '/api/placeholder/400/300',
      title: 'Product Animation',
      subtitle: '3D product showcase',
      description: 'Smooth 3D product animations for e-commerce presentations.',
      color: 'bg-indigo-600'
    },
    {
      id: 16,
      image: '/api/placeholder/400/300',
      title: 'Brand Animation',
      subtitle: 'Logo animations',
      description: 'Dynamic brand animations for digital marketing campaigns.',
      color: 'bg-red-500'
    }
  ]
};

export default function ModernPortfolio() {
  const [activeTab, setActiveTab] = useState('Website Development');
  const tabs = Object.keys(portfolioData);

  return (
    <section className="first py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-12">
          <div className="mb-8 lg:mb-0">
            <p className="text-gray-600 text-lg uppercase font-semibold">check our</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl leading-[1] font-normal text-gray-900">
              <span className="font-normal font-jersey">finished projects</span>
            </h2>
          </div>

          <div className="text-right max-w-xs">
            <p className="text-gray-800 text-sm leading-relaxed">
              We have worked on over 20 project in the last 3 months, here are some of them:
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition-colors duration-200 border-b-2 ${activeTab === tab
                ? 'text-gray-900 border-gray-900'
                : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {portfolioData[activeTab].map((project) => (
            <div
              key={project.id}
              className={` bg-white rounded-2xl overflow-hidden group cursor-pointer transition-transform duration-300 shadow-lg hover:shadow-xl`}
            >
              <div className="">
                <div className="relative w-full h-[15rem] rounded-lg overflow-hidden">
                  {/* Blurred background */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center filter blur-md scale-110"
                    style={{
                      backgroundImage: `url(${project.image})`
                    }}
                  />
                  {/* Main image */}
                  <img
                    src={project.image}
                    alt={project.title}
                    className="relative z-10 w-full h-full object-contain"
                  />
                </div>

                <div className="p-6">
                  <h3 className="text-lg font-semibold mb-1">{project.title}</h3>
                  <p className="text-gray-700 text-sm mb-3">{project.subtitle}</p>
                  <p className=" text-xs mb-4 ">{project.description}</p>

                  {/* <button className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium transition-colors group">
                    view case
                    <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                  </button> */}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-16 pt-12 border-t border-gray-200">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-2">10+</div>
              <div className="text-gray-600 text-sm">Projects Completed</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-2">1+</div>
              <div className="text-gray-600 text-sm">Years in Business</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-2">2</div>
              <div className="text-gray-600 text-sm">Countries Served</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-gray-900 mb-2">1</div>
              <div className="text-gray-600 text-sm">Mission To bring your vision to life</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}